import { useCallback, useMemo, useRef } from 'react';
import { useUpdateEffect } from 'react-use';
import { colorsButton } from 'app/colors';
import { useTranslation } from 'react-i18next';
import useModal from 'components/Modals/useModal';
import ConfirmDeletionModal from 'components/Modals/presets/ConfirmDeletion';
import { IconSizing } from 'components/Kizen/Icon';
import IconButton from 'components/Kizen/IconButton';
import Icon from 'components/Kizen/Icon';
import { KizenTypography } from 'app/typography';
import { mapValues } from 'lodash';
import useFlashState from 'hooks/useFlashState';
import { getDataQAForInput } from 'components/Inputs/helpers';
import {
  Select,
  StyledDiv,
  StyledRowItemCard,
  StyledRowItemSwitch,
  StyledRowItemWrapper,
} from './styled';
import TextInput from 'components/Inputs/TextInput';
import useField from 'hooks/useField';

const ONE_FIFTH = 238;
const ONE_FOURTH = 302;

export const RowItem = ({
  errors,
  editable,
  onConfirmDelete,
  allModelOptions,
  value,
  onChange,
  onClickRemove,
  objectFields,
  entityName: entityNameProp,
  relation,
  allRelationTypeOptions,
  index,
  validationProps,
  relationshipSuppressed,
  dataQa,
}) => {
  const isDisabled = !editable;
  const relatedObjectField = useMemo(
    () => objectFields && objectFields.find((el) => el.id === value.fieldId),
    [value, objectFields]
  );

  const [displayName, setDisplayName] = useField(value.field?.displayName);

  const [relatedName, setRelatedName] = useField(
    value?.field?.relation?.relatedName
  );

  const [confirmDeletionModalProps, , confirmDeletionModal] = useModal({
    handleSubmit: async () => {
      await onConfirmDelete(relatedObjectField, relation);
    },
  });

  const handleSelectAction = () => {
    confirmDeletionModal.show();
  };

  const valueRef = useRef();
  valueRef.current = value;

  const { t } = useTranslation();

  const relationTypeRef = useRef();
  const [relationTypeError, flashRelationTypeError] = useFlashState();

  useUpdateEffect(() => {
    if (errors.validation && !valueRef.current.relationType) {
      flashRelationTypeError(true);
      const inputRef = mapValues(relationTypeRef, 'select.inputRef');
      if (inputRef.current) {
        inputRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    }
  }, [errors, flashRelationTypeError]);

  const displayNameValidationKey = `${relation}.[${index}].field.displayName`;
  const reverseNameValidationKey = `${relation}.[${index}].field.relation.relatedName`;

  const isSuppressed =
    !!value?.field?.isSuppressed ||
    !!value?.field?.relation?.inverseRelationSuppressed;

  const handleChangeObject = useCallback(
    ({ value: relatedObject, entityName, isContact }) => {
      const dictionary = allRelationTypeOptions.find(
        ({ value: valueId }) => valueId === value.relationType
      );
      onChange(
        {
          ...value,
          relatedObject,
          entityName,
          isContact,
          relationType: value.relationType,
          field: {
            ...value.field,
            displayName: dictionary.name(entityName),
            relation: {
              ...value?.field?.relation,
              relatedName: dictionary.reverseName(entityNameProp),
            },
          },
        },
        true
      );
    },
    [allRelationTypeOptions, entityNameProp, onChange, value]
  );

  const handleChangeRelationship = useCallback(
    ({ value: relationType }) => {
      const dictionary = allRelationTypeOptions.find(
        ({ value: valueId }) => valueId === relationType
      );
      onChange({
        ...value,
        relationType,
        field: {
          ...value.field,
          displayName: dictionary.name(value.entityName),
          relation: {
            ...value.field?.relation,
            relatedName: dictionary.reverseName(entityNameProp),
          },
        },
      });
    },
    [allRelationTypeOptions, entityNameProp, onChange, value]
  );

  return (
    <>
      <StyledRowItemWrapper data-qa={dataQa}>
        <IconButton
          title={t('Delete')}
          sizing="dense"
          color={colorsButton.iconGray}
          onClick={!isDisabled ? onClickRemove : handleSelectAction}
        >
          <IconSizing size="15px">
            <Icon icon="delete" />
          </IconSizing>
        </IconButton>
        <StyledRowItemCard hasError={errors.validation}>
          <StyledDiv
            width={ONE_FOURTH}
            {...getDataQAForInput('object-name', 'dropdown')}
          >
            <Select
              placeholder={t('Choose Object')}
              value={value.relatedObject}
              onChange={handleChangeObject}
              label={t('Related Object')}
              labelInfo={
                isSuppressed
                  ? t(
                      'Timeline sharing is disabled when the related field is suppressed'
                    )
                  : ''
              }
              options={allModelOptions}
              disabled={isDisabled}
            />
          </StyledDiv>
          <StyledDiv
            width={ONE_FOURTH}
            {...getDataQAForInput('relationship', 'dropdown')}
          >
            <Select
              ref={relationTypeRef}
              placeholder={t('Choose Relationship')}
              label={t('Relationship Type')}
              error={relationTypeError}
              value={
                value.relationType === 'additional_for'
                  ? 'additional'
                  : value.relationType
              }
              onChange={handleChangeRelationship}
              options={
                relation === 'relatedObjectsReverse'
                  ? isDisabled
                    ? allRelationTypeOptions
                    : allRelationTypeOptions.slice(0, -1)
                  : allRelationTypeOptions
              }
              disabled={isDisabled}
            />
          </StyledDiv>
          <StyledDiv
            width={ONE_FOURTH}
            {...getDataQAForInput('relationship_name')}
          >
            <TextInput
              label={t('Relationship Name')}
              placeholder={t('Enter Relationship Name')}
              value={displayName}
              onChange={setDisplayName}
              {...validationProps(displayNameValidationKey)}
              onBlur={() => {
                onChange({
                  ...value,
                  field: {
                    modified: !displayName,
                    ...value?.field,
                    displayName,
                    name: displayName,
                  },
                });
              }}
              required
            />
          </StyledDiv>
          <StyledDiv
            width={ONE_FOURTH}
            {...getDataQAForInput('reverse_relationship_name')}
          >
            <TextInput
              label={t('Reverse Relationship Name')}
              placeholder={t('Enter Reverse Relationship Name')}
              value={relatedName}
              onChange={setRelatedName}
              {...validationProps(reverseNameValidationKey)}
              onBlur={() => {
                onChange({
                  ...value,
                  field: {
                    modified: !relatedName,
                    ...value?.field,
                    relation: {
                      ...value?.field?.relation,
                      relatedName,
                    },
                  },
                });
              }}
              required
            />
          </StyledDiv>
          <StyledDiv
            width={ONE_FIFTH}
            {...getDataQAForInput('rollup-timeline')}
          >
            <StyledRowItemSwitch
              disabled={isSuppressed}
              checked={value?.field?.relation?.inverseRelationRollupTimeline}
              onChange={(ev) => {
                onChange({
                  ...value,
                  field: {
                    ...value?.field,
                    relation: {
                      ...value?.field?.relation,
                      inverseRelationRollupTimeline: ev.target.checked,
                    },
                  },
                });
              }}
              label={t('Share Timeline To Related?')}
              tooltip={t(
                "Timeline entries will be shared from this object's record to the associated related object's record."
              )}
              textPlacement="regular-top"
            />
          </StyledDiv>
          <StyledDiv
            width={ONE_FIFTH}
            {...getDataQAForInput('inverse-relation-rollup-lead-sources')}
          >
            <StyledRowItemSwitch
              disabled={isSuppressed}
              checked={value?.field?.relation?.rollupTimeline}
              onChange={(ev) => {
                onChange({
                  ...value,
                  field: {
                    ...value?.field,
                    relation: {
                      ...value?.field?.relation,
                      rollupTimeline: ev.target.checked,
                    },
                  },
                });
              }}
              label={t('Share Timeline From Related?')}
              tooltip={t(
                "Timeline entries will be shared from the related object's record and displayed on associated records of this object."
              )}
              textPlacement="regular-top"
            />
          </StyledDiv>
          <StyledDiv
            width={ONE_FIFTH}
            {...getDataQAForInput('rollup-leadsources')}
          >
            <StyledRowItemSwitch
              disabled={isSuppressed}
              checked={value?.field?.relation?.inverseRelationRollupLeadsources}
              onChange={(ev) => {
                onChange({
                  ...value,
                  field: {
                    ...value?.field,
                    relation: {
                      ...value?.field?.relation,
                      inverseRelationRollupLeadsources: ev.target.checked,
                    },
                  },
                });
              }}
              label={t('Share Lead Sources To Related?')}
              tooltip={t(
                "Lead Sources will be shared from this object's record to the associated related object's record."
              )}
              textPlacement="regular-top"
            />
          </StyledDiv>
          <StyledDiv
            width={ONE_FIFTH}
            {...getDataQAForInput('inverse-relation-rollup-lead-sources')}
          >
            <StyledRowItemSwitch
              disabled={isSuppressed}
              checked={value?.field?.relation?.rollupLeadsources}
              onChange={(ev) => {
                onChange({
                  ...value,
                  field: {
                    ...value?.field,
                    relation: {
                      ...value?.field?.relation,
                      rollupLeadsources: ev.target.checked,
                    },
                  },
                });
              }}
              label={t('Share Lead Sources From Related?')}
              tooltip={t(
                "Lead Sources will be shared from the related object's record and displayed on associated records of this object."
              )}
              textPlacement="regular-top"
            />
          </StyledDiv>
          <StyledDiv
            width={ONE_FIFTH}
            {...getDataQAForInput('inverse-relation-suppressed')}
          >
            <StyledRowItemSwitch
              disabled={relationshipSuppressed || value?.field?.isSuppressed}
              checked={value?.field?.relation?.inverseRelationSuppressed}
              onChange={(ev) => {
                onChange({
                  ...value,
                  field: {
                    ...value?.field,
                    relation: {
                      ...value?.field?.relation,
                      inverseRelationSuppressed: ev.target.checked,
                      ...(ev.target.checked && {
                        rollupTimeline: false,
                        rollupLeadsources: false,
                        inverseRelationRollupTimeline: false,
                        inverseRelationRollupLeadsources: false,
                      }),
                    },
                  },
                });
              }}
              label={t('Suppress Related Field?')}
              tooltip={t(
                "When suppressed, the related object will not have a relationship field visible, nor will updates to this relationship appear in that object's record timelines. This is not reversible."
              )}
              textPlacement="regular-top"
            />
          </StyledDiv>
        </StyledRowItemCard>
      </StyledRowItemWrapper>
      <ConfirmDeletionModal {...confirmDeletionModalProps}>
        <KizenTypography>
          {t(
            'This will permanently delete the Field and all associated data with it ' +
              'from your database. Since this is a relationship field, it will also ' +
              'delete the other related field'
          )}{' '}
          {relatedObjectField?.relation?.relatedObjectName}:{' '}
          {relatedObjectField?.displayName} {t('and all of its data.')}{' '}
          {t('This action cannot be undone.')}
        </KizenTypography>
      </ConfirmDeletionModal>
    </>
  );
};
